import React, { useState, useEffect, useRef } from "react";
import Carousel from "react-bootstrap/Carousel";
import "./Posts.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { AiOutlineEye, AiOutlineLike } from "react-icons/ai";
import { FaRegCommentDots, FaRegFolderOpen } from "react-icons/fa";
import { BsFillSendFill } from "react-icons/bs";
import {
  getAllPostsWithPagination,
  reactToPost,
  getEmployeeDetails,
  commentOnPost,
  viewPostStart,
  viewPostEnd,
  getPostViewDetails,
} from "../../../api/ApiConfig";
import SuggestionModal from "../../global/CommentOnPost";
import ReactionPopup from "../reactions/Reactions";
import { getCookie } from "../../../utils/CookiesUtils";
import { 
    FaUsers, 
    FaTrophy, 
    FaCalendarAlt, 
    FaNewspaper, 
    FaHeart, 
    FaBullhorn, 
    FaEllipsisH 
} from 'react-icons/fa';

const tabItems = [
  { name: "All", icon:<FaUsers/> },
  { name: "New Win", icon: <FaTrophy/> },
  { name: "Events", icon: <FaCalendarAlt/> },
  { name: "News", icon: <FaNewspaper/>},
  { name: "Appreciation", icon: <FaHeart/> },
  { name: "Announcement", icon: <FaBullhorn/>},
  { name: "Others", icon: <FaEllipsisH/>},
];
const emojiList = [
  { id: "heart", emoji: "❤️" },
  { id: "smile", emoji: "😊" },
  { id: "like", emoji: "👍" },
  { id: "celebrate", emoji: "🎉" },
  { id: "clap", emoji: "👏" },
];

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase();
};

const ViewersModal = ({ show, onHide, viewersData }) => {
  if (!show) return null;

  return (
    <div className="viewers-modal-backdrop">
      <div className="viewers-modal">
        <div className="modal-header">
          <h5>Post Viewers</h5>
          <button className="close-btn" onClick={onHide}>
            ✖
          </button>
        </div>
        <div className="viewers-users">
          {viewersData.length > 0 ? (
            viewersData.map((viewer, index) => (
              <div className="viewers-user" key={index}>
                <div className="avatar">{getInitials(viewer.emp_name)}</div>
                <div className="info">
                  <div className="name">{viewer.emp_name}</div>
                  <div className="subtext">{viewer.viewed_at}</div>
                </div>
              </div>
            ))
          ) : (
            <p className="no-viewers-message">No viewers yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Posts() {
  const [selectedTab, setSelectedTab] = useState("All");
  const [emojiReactions, setEmojiReactions] = useState({});
  const [hoveredPost, setHoveredPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [showComments, setShowComments] = useState({});
  const [showSuggestionModalForPost, setShowSuggestionModalForPost] =
    useState(false);
  const [sendRawData, setSendRawData] = useState(null);
  const [showReactionPopup, setShowReactionPopup] = useState(false);
  const [selectedPostIdForPopup, setSelectedPostIdForPopup] = useState(null);
  const [viewCounts, setViewCounts] = useState({});
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState([]);
  const [selectedPostIdForViews, setSelectedPostIdForViews] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 15;

  const postsListRef = useRef(null);
  const viewTimers = useRef({});
  const observerRef = useRef(null);
  const lastObservedPostId = useRef(null);

  const handleOpenModal = () => {
    setShowSuggestionModalForPost(true);
  };

  // Handle video click events to prevent carousel navigation
  const handleVideoClick = (e) => {
    e.stopPropagation();
  };

  // Handle video mouse events to prevent carousel navigation
  const handleVideoMouseDown = (e) => {
    e.stopPropagation();
  };

  const fetchPosts = async (page, tab) => {
    setIsLoading(true);
    try {
      let response;
      const baseOptions = { page: page, page_size: pageSize };
      let postType = "";

      switch (tab) {
        case "New Win":
          postType = "new_wins";
          break;
        case "Events":
          postType = "events";
          break;
        case "News":
          postType = "news";
          break;
        case "Appreciation":
          postType = "appreciations";
          break;
        case "Announcement":
          postType = "announcements";
          break;
        case "Others":
          postType = "others";
          break;
        default:
          postType = "";
      }

      response = await getAllPostsWithPagination({
        ...baseOptions,
        ...(postType && { post_type: postType }),
      });

      const newPosts = response?.data?.posts || [];
      if (page === 1) {
        setPosts(newPosts);
      } else {
        setPosts((prevPosts) => [...prevPosts, ...newPosts]);
      }
      setHasMore(newPosts.length === pageSize);
    } catch (error) {
      console.error("Error fetching posts:", error);
      if (page === 1) {
        setPosts([]);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const startPostView = async (postId) => {
    if (viewTimers.current[postId]) return;

    try {
      const sid = getCookie("sid");
      if (!sid) {
        console.error("Session ID (sid) not found in cookies");
        return;
      }

      const userData = {
        posts_id: postId,
        sess_key: sid,
      };

      const startTime = Date.now();
      viewTimers.current[postId] = { startTime };

      const response = await viewPostStart(userData);

      setViewCounts((prev) => ({
        ...prev,
        [postId]: response.view_counts || 0,
      }));
    } catch (error) {
      console.error("Error starting post view:", error);
      delete viewTimers.current[postId];
    }
  };

  const endPostView = async (postId) => {
    const timer = viewTimers.current[postId];
    if (!timer) return;

    try {
      const endTime = Date.now();
      const duration = endTime - timer.startTime;

      if (duration < 1000) {
        delete viewTimers.current[postId];
        console.log(
          `View of post ${postId} was too short (${duration}ms), not logging.`
        );
        return;
      }

      const sid = getCookie("sid");
      if (!sid) {
        console.error("Session ID (sid) not found in cookies");
        return;
      }

      const userData = {
        posts_id: postId,
        duration: Math.round(duration / 1000),
        sess_key: sid,
      };

      await viewPostEnd(userData);
      delete viewTimers.current[postId];
      console.log(
        `View of post ${postId} ended. Duration: ${Math.round(
          duration / 1000
        )}s`
      );
    } catch (error) {
      console.error("Error ending post view:", error);
    }
  };

  const handleViewersClick = async (postId) => {
    try {
      const response = await getPostViewDetails({ posts_id: postId });
      const viewers = response.data || [];
      setViewersData(viewers);
      setSelectedPostIdForViews(postId);
    } catch (error) {
      console.error("Error fetching viewers:", error);
      setViewersData([]);
    } finally {
      setShowViewersModal(true);
    }
  };

  const handleCloseViewersModal = () => {
    setShowViewersModal(false);
    setViewersData([]);
    setSelectedPostIdForViews(null);
  };

  useEffect(() => {
    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const postId = entry.target.dataset.postid;
        if (postId) {
          if (entry.isIntersecting) {
            startPostView(postId);
          } else {
            endPostView(postId);
          }
        }
      });
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: postsListRef.current,
      threshold: 0.5,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        Object.keys(viewTimers.current).forEach(endPostView);
      }
    };
  }, []);

  useEffect(() => {
    if (observerRef.current && posts.length > 0) {
      const postElements =
        postsListRef.current.querySelectorAll("[data-postid]");
      postElements.forEach((el) => {
        if (!el.isObserved) {
          observerRef.current.observe(el);
          el.isObserved = true;
        }
      });
    }
  }, [posts]);

  const toggleComments = (postId) => {
    setShowComments((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  const fetchEmployeeData = async () => {
    try {
      const data = await getEmployeeDetails();
      if (data?.emp_id && data?.email) {
        setEmployeeDetails(data);
      } else {
        setEmployeeDetails({});
      }
    } catch (error) {
      setEmployeeDetails({});
    }
  };

  useEffect(() => {
    setPosts([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchPosts(1, selectedTab);
    if (postsListRef.current) {
      postsListRef.current.scrollTop = 0;
    }
  }, [selectedTab]);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (postsListRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = postsListRef.current;
        if (
          scrollTop + clientHeight >= scrollHeight - 500 &&
          !isLoading &&
          hasMore
        ) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      }
    };

    const currentRef = postsListRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isLoading, hasMore]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchPosts(currentPage, selectedTab);
    }
  }, [currentPage]);

  const handleEmojiSelect = async (postId, emojiId) => {
    const selectedEmoji = emojiList.find((e) => e.id === emojiId)?.emoji;
    setEmojiReactions((prev) => ({ ...prev, [postId]: selectedEmoji }));
    setHoveredPost(null);

    try {
      await reactToPost({
        posts_id: postId,
        reaction_type: emojiId,
      });
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.posts_id === postId
            ? {
                ...post,
                reaction_details: {
                  ...post.reaction_details,
                  [emojiId]: (post.reaction_details?.[emojiId] || 0) + 1,
                },
              }
            : post
        )
      );
      fetchPosts(1, selectedTab);
    } catch (error) {
      console.error("Failed to react to post:", error);
    }
  };

  const handleCommentChange = (postId, text) => {
    setCommentInputs((prev) => ({
      ...prev,
      [postId]: text,
    }));
  };

  const handleCommentSubmit = async (postId) => {
    const commentText = commentInputs[postId]?.trim();
    if (!commentText) return;

    try {
      await commentOnPost({
        posts_id: postId,
        comment_text: commentText,
      });
      setCommentInputs((prev) => ({
        ...prev,
        [postId]: "",
      }));
      setShowComments((prev) => ({
        ...prev,
        [postId]: true,
      }));
      fetchPosts(1, selectedTab);
    } catch (error) {
      console.error("Failed to comment on post:", error);
    }
  };

  // Function to convert links in a string to clickable React elements
  const parseTextWithLinks = (text) => {
    // Regex to match common URLs (http/https, optional www, domains, paths)
    const urlRegex =
      /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])|(\bwww\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

    const parts = [];
    let lastIndex = 0;

    // Find all matches in the text
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0];
      const startIndex = match.index;

      // 1. Push the preceding text (if any)
      if (startIndex > lastIndex) {
        parts.push(text.substring(lastIndex, startIndex));
      }

      // 2. Push the clickable link element
      // Ensure 'http://' or 'https://' is prepended if only 'www.' was matched
      const href =
        url.startsWith("http") ||
        url.startsWith("ftp") ||
        url.startsWith("file")
          ? url
          : `http://${url}`;

      parts.push(
        <a
          key={startIndex}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      );

      lastIndex = urlRegex.lastIndex;
    }

    // 3. Push the remaining text after the last link
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts;
  };

  return (
    <div className="posts-wrapper">
    <div className="tabs-container">
      <div className="tabs-list">
        {tabItems.map((tab) => (
          <button
            key={tab.name}
            className={`tab-button-new ${selectedTab === tab.name ? "active" : ""}`}
            onClick={() => setSelectedTab(tab.name)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.name}</span>
          </button>
        ))}
      </div>
    </div>

      <div className="posts-list" ref={postsListRef}>
        {posts.map((post) => (
          <div className="post-card" key={post._id} data-postid={post.posts_id}>
            <div className="post-header">
              <div className="avatar">{getInitials(post.emp_name)}</div>
              <div className="post-info">
                <span className="username">{post.emp_name}</span>
                <span className="timestamp">{post.created_on}</span>
              </div>
            </div>

            <div className="post-text mt-1">
              {/* Use the parsing function here */}
              {parseTextWithLinks(post.posts_contents)}
            </div>

            {post.posts_attachments?.length > 0 && (
              <Carousel
                interval={playingVideoId ? null : 5000}
                className="post-carousel"
                touch={false} // Disable touch/swipe for better video control
              >
                {post.posts_attachments.map((media, i) => {
                  const fileUrl = media.content;
                  const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(
                    fileUrl.split("?")[0]
                  );
                  const videoId = `${post.posts_id}-${i}`;

                  return (
                    <Carousel.Item key={i} className="carousel-item-container">
                      <div className="slide-counter">
                        {i + 1} / {post.posts_attachments.length}
                      </div>

                      {isVideo ? (
                        <div
                          className="video-wrapper"
                          onClick={handleVideoClick}
                          onMouseDown={handleVideoMouseDown}
                        >
                          <video
                            controls
                            className="post-media"
                            onClick={handleVideoClick}
                            onMouseDown={handleVideoMouseDown}
                            onPlay={() => setPlayingVideoId(videoId)}
                            onPause={() => {
                              if (playingVideoId === videoId) {
                                setPlayingVideoId(null);
                              }
                            }}
                            onEnded={() => {
                              if (playingVideoId === videoId) {
                                setPlayingVideoId(null);
                              }
                            }}
                            style={{ pointerEvents: "auto" }} // Ensure video controls work
                          >
                            <source
                              src={fileUrl}
                              type={`video/${
                                fileUrl.split(".").pop().split("?")[0]
                              }`}
                            />
                          </video>
                        </div>
                      ) : (
                        <div className="image-wrapper">
                          <img
                            className="post-media"
                            src={fileUrl}
                            alt={`slide-${i}`}
                            onError={(e) => {
                              e.target.src = "fallback-image-url.jpg";
                              e.target.className =
                                "post-media post-media-fallback";
                            }}
                          />
                        </div>
                      )}
                    </Carousel.Item>
                  );
                })}
              </Carousel>
            )}

            <div className="post-stats">
              <div
                className="reaction-summary"
                onClick={() => {
                  setSelectedPostIdForPopup(post.posts_id);
                  setShowReactionPopup(true);
                }}
              >
                {(() => {
                  const emojisUsed = [];
                  let totalCount = 0;

                  emojiList.forEach((emojiItem) => {
                    const count = post.reaction_details?.[emojiItem.id] || 0;
                    if (count > 0) {
                      emojisUsed.push(emojiItem.emoji);
                      totalCount += count;
                    }
                  });

                  if (totalCount === 0) return null;

                  return (
                    <span className="emoji-count-combined">
                      {emojisUsed.join(" ")} {totalCount}
                    </span>
                  );
                })()}
              </div>
              <div
                className="view-count"
                onClick={() => handleViewersClick(post.posts_id)}
              >
                <AiOutlineEye size={16} />
                <span>{viewCounts[post.posts_id] || 0} </span>
              </div>
            </div>

            <div className="view-count"></div>
            <div className="post-actions">
              <div
                className="like-wrapper"
                onMouseEnter={() => setHoveredPost(post._id)}
                onMouseLeave={() => setHoveredPost(null)}
              >
                <button className="like">
                  {emojiReactions[post._id] ? (
                    <span className="emoji-selected">
                      {emojiReactions[post._id]}
                      <span className="emoji-id">
                        (
                        {Object.keys(post.reaction_details || {}).find(
                          (key) =>
                            emojiList.find((e) => e.id === key)?.emoji ===
                            emojiReactions[post._id]
                        )}
                        )
                      </span>
                    </span>
                  ) : (
                    (() => {
                      const currentUserEmpId = employeeDetails.emp_id;
                      const userReaction = Object.entries(
                        post.reaction_count_details || {}
                      ).find(([reaction, reactionData]) => {
                        if (reactionData?.reactions) {
                          return reactionData.reactions.some(
                            (reactor) => reactor.emp_id === currentUserEmpId
                          );
                        }
                        return (
                          Array.isArray(reactionData) &&
                          reactionData.some(
                            (reactor) => reactor.emp_id === currentUserEmpId
                          )
                        );
                      });

                      if (userReaction) {
                        const [reactionId] = userReaction;
                        const reactionEmoji = emojiList.find(
                          (e) => e.id === reactionId
                        )?.emoji;
                        return (
                          <span className="emoji-selected">
                            {reactionEmoji}
                            <span className="emoji-id">{reactionId}</span>
                          </span>
                        );
                      } else {
                        return (
                          <>
                            <AiOutlineLike
                              size={15}
                              style={{ marginRight: 6 }}
                            />
                            Likes
                          </>
                        );
                      }
                    })()
                  )}
                </button>

                {hoveredPost === post._id && (
                  <div className="emoji-popup">
                    {emojiList.map((item) => (
                      <span
                        key={item.id}
                        className="emoji-option"
                        onClick={() =>
                          handleEmojiSelect(post.posts_id, item.id)
                        }
                        title={item.id}
                      >
                        {item.emoji}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="comment"
                onClick={() => {
                  toggleComments(post.posts_id);
                }}
              >
                <FaRegCommentDots size={18} style={{ marginRight: 6 }} />
                Comments ({post.comments_count || 0})
              </button>
            </div>

            <div className="post-comment">
              <div className="avatar">{getInitials(employeeDetails.name)}</div>
              <input
                type="text"
                className="comment-input"
                placeholder="Write a comment..."
                onClick={handleOpenModal}
                value={commentInputs[post.posts_id] || ""}
                onChange={(e) =>
                  handleCommentChange(post.posts_id, e.target.value)
                }
                readOnly={true}
                onFocus={() => {
                  setSendRawData({
                    postId: post.posts_id,
                    postType: post.posts_type,
                  });
                }}
                onKeyPress={(e) =>
                  e.key === "Enter" && handleCommentSubmit(post.posts_id)
                }
              />

              <button
                className="send-btn-comment"
                onClick={() => handleCommentSubmit(post.posts_id)}
              >
                <BsFillSendFill color="#000000" />
              </button>
            </div>

            {showComments[post.posts_id] && (
              <div className="comments-section">
                {post.comments?.length > 0 ? (
                  post.comments.map((comment, index) => (
                    <div className="comment-item" key={index}>
                      <div className="avatar">
                        {getInitials(comment.emp_name)}
                      </div>
                      <div className="comment-content mx-3 mt-1">
                        <div className="comment-user">{comment.emp_name}</div>
                        <div className="comment-text">
                          {comment.comment_text}
                        </div>
                        <div className="comment-time">{comment.created_on}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">No comments yet</div>
                )}
              </div>
            )}
          </div>
        ))}

        {isLoading && <div className="loading-spinner">Loading posts...</div>}
        {!isLoading && posts.length === 0 && (
          <div className="no-posts">
            <div className="no-posts-icon">
              <FaRegFolderOpen />
            </div>
            <p>No Posts Available</p>
          </div>
        )}
        {showSuggestionModalForPost && (
          <SuggestionModal
            postId={sendRawData?.postId}
            postType={sendRawData?.postType}
            onSelect={(selectedSuggestion) =>
              setCommentInputs((prev) => ({
                ...prev,
                [sendRawData.postId]: selectedSuggestion,
              }))
            }
            onClose={() => setShowSuggestionModalForPost(false)}
          />
        )}

        {showReactionPopup && selectedPostIdForPopup && (
          <ReactionPopup
            postId={selectedPostIdForPopup}
            onClose={() => {
              setShowReactionPopup(false);
              setSelectedPostIdForPopup(null);
            }}
          />
        )}
      </div>
      <ViewersModal
        show={showViewersModal}
        onHide={handleCloseViewersModal}
        viewersData={viewersData}
      />
    </div>
  );
}
